import React, { useMemo } from "react";
import { Box, Stack, Tooltip, Typography } from "@mui/material";

import { formatStockCountNumber } from "../stockCountQtyFormat";

const toArray = (value) => {
  if (Array.isArray(value)) return value;
  if (Array.isArray(value?.results)) return value.results;
  return [];
};

const toNumber = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const pluralizeSpanish = (name, qty) => {
  const clean = String(name || "unidad").trim().toLowerCase();
  if (Math.abs(toNumber(qty) - 1) <= 0.000001) return clean;
  if (clean === "c/u" || clean === "u" || clean === "und") return "unidades";
  if (clean.endsWith("z")) return `${clean.slice(0, -1)}ces`;
  if (/[aeiou]$/.test(clean)) return `${clean}s`;
  return `${clean}es`;
};

const normalizeBaseUnitName = (name) => {
  const clean = String(name || "").trim();
  if (!clean) return "unidad";
  const lower = clean.toLowerCase();
  if (["c/u", "u", "und"].includes(lower) || lower.includes("unidad")) return "unidad";
  return lower;
};

export const getProductPresentationSummaryRows = (product, presentationsSummary) => {
  const sourceRows = toArray(presentationsSummary).length
    ? toArray(presentationsSummary)
    : toArray(product?.presentations_summary).length
      ? toArray(product?.presentations_summary)
      : toArray(product?.presentations);

  const rows = sourceRows
    .map((row) => {
      const name = row?.name || row?.format_name || row?.product_format?.name || row?.label;
      const qtyBase = row?.qty_base ?? row?.qty_in_base;
      if (!name || toNumber(qtyBase) <= 0) return null;
      return {
        name,
        qty_base: qtyBase,
        qtyNumber: toNumber(qtyBase),
      };
    })
    .filter(Boolean);

  if (rows.length === 0 && product?.max_presentation_name && toNumber(product?.max_presentation_qty_base) > 0) {
    rows.push({
      name: product.max_presentation_name,
      qty_base: product.max_presentation_qty_base,
      qtyNumber: toNumber(product.max_presentation_qty_base),
      maxOnlyFallback: true,
    });
  }
  if (rows.length === 0 && product?.max_presentation_name && toNumber(product?.max_presentation_qty_in_base) > 0) {
    rows.push({
      name: product.max_presentation_name,
      qty_base: product.max_presentation_qty_in_base,
      qtyNumber: toNumber(product.max_presentation_qty_in_base),
      maxOnlyFallback: true,
    });
  }

  const seen = new Set();
  return rows
    .filter((row) => {
      const key = `${String(row.name).toLowerCase()}:${row.qtyNumber}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    })
    .sort((left, right) => left.qtyNumber - right.qtyNumber || String(left.name).localeCompare(String(right.name)));
};

export const getProductPackagingMeta = (product, presentationsSummary) => {
  const rows = getProductPresentationSummaryRows(product, presentationsSummary);
  const fallbackMax = rows[rows.length - 1] || null;
  const maxName = product?.max_presentation_name || fallbackMax?.name || null;
  const maxQty =
    product?.max_presentation_qty_base ??
    product?.max_presentation_qty_in_base ??
    fallbackMax?.qty_base ??
    null;
  const basePresentation = rows.find((row) => Math.abs(row.qtyNumber - 1) <= 0.000001) || rows[0];
  const baseUnit =
    !basePresentation || basePresentation.maxOnlyFallback || Math.abs(basePresentation.qtyNumber - 1) > 0.000001
      ? "unidad"
      : normalizeBaseUnitName(basePresentation.name);

  return {
    rows,
    maxName,
    maxQty,
    baseUnit,
  };
};

export const getProductPackagingText = (product, presentationsSummary) => {
  const { maxName, maxQty, baseUnit } = getProductPackagingMeta(product, presentationsSummary);
  if (!maxName || toNumber(maxQty) <= 0) return "Empaque mayor: No definido";
  return `Empaque mayor: ${maxName} (${formatStockCountNumber(maxQty)} ${pluralizeSpanish(baseUnit, maxQty)})`;
};

function ProductTooltipContent({ rows, baseUnit }) {
  const visibleRows = rows.length > 0 ? rows : [{ name: "Unidad", qty_base: 1, qtyNumber: 1 }];
  return (
    <Box>
      <Typography variant="caption" sx={{ fontWeight: 800, display: "block", mb: 0.4 }}>
        {visibleRows.length === 1 ? "Presentacion disponible" : "Presentaciones disponibles"}
      </Typography>
      <Stack spacing={0.2}>
        {visibleRows.map((row) => (
          <Typography key={`${row.name}-${row.qty_base}`} variant="caption" display="block">
            - {row.name} = {formatStockCountNumber(row.qty_base)} {pluralizeSpanish(baseUnit, row.qty_base)}
          </Typography>
        ))}
      </Stack>
    </Box>
  );
}

export function ProductFormatLine({ product, presentationsSummary, sx = {} }) {
  const meta = useMemo(
    () => getProductPackagingMeta(product, presentationsSummary),
    [presentationsSummary, product]
  );
  return (
    <Tooltip title={<ProductTooltipContent rows={meta.rows} baseUnit={meta.baseUnit} />} arrow>
      <Typography
        variant="caption"
        color="text.secondary"
        display="block"
        noWrap
        sx={{ lineHeight: 1.25, ...sx }}
      >
        {getProductPackagingText(product, presentationsSummary)}
      </Typography>
    </Tooltip>
  );
}

function ProductFormatSummary({
  product,
  presentationsSummary,
  name,
  code,
  showCode = true,
  nameVariant = "body2",
  codeVariant = "caption",
  nameSx = {},
  lineSx = {},
}) {
  const displayName = name || product?.description || product?.product_description || "Producto";
  const displayCode = code || product?.code || product?.product_code || "";
  const meta = useMemo(
    () => getProductPackagingMeta(product, presentationsSummary),
    [presentationsSummary, product]
  );

  return (
    <Tooltip title={<ProductTooltipContent rows={meta.rows} baseUnit={meta.baseUnit} />} arrow>
      <Box sx={{ minWidth: 0 }}>
        <Typography variant={nameVariant} sx={{ fontWeight: 700, lineHeight: 1.25, ...nameSx }} noWrap>
          {displayName}
        </Typography>
        {showCode ? (
          <Typography variant={codeVariant} color="text.secondary" display="block" noWrap sx={{ lineHeight: 1.2 }}>
            {displayCode || "Sin codigo"}
          </Typography>
        ) : null}
        <Typography
          variant="caption"
          color="text.secondary"
          display="block"
          noWrap
          sx={{ lineHeight: 1.25, ...lineSx }}
        >
          {getProductPackagingText(product, presentationsSummary)}
        </Typography>
      </Box>
    </Tooltip>
  );
}

export default ProductFormatSummary;
